// src/pages/auth/SignIn.js
// for login with email and password
import React, { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useMainPageTitle } from "../../contexts/PageContext";
import { useAuth } from "../../contexts/authContext";
import { useNavigate, useSearchParams } from "react-router-dom";
import SignInForm from "../../components/pages/auth/SignInForm";
import GoogleBtn from "./components/googleBtn";
import { Link } from "react-router-dom";

export default function SignIn() {
  const { t } = useTranslation();
  const { updatePageTitle } = useMainPageTitle();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [authMessage, setAuthMessage] = useState('');
  const [authError, setAuthError] = useState('');

  /**
   * Handle successful Google authentication
   */
  const handleGoogleAuthSuccess = useCallback(async (token, userDataString, isNewUser) => {
    try {
      setAuthMessage(t("auth.completingGoogleSignIn"));
      
      // Parse user data if provided
      let userData = null;
      if (userDataString) {
        try {
          userData = JSON.parse(decodeURIComponent(userDataString));
        } catch (e) {
          console.error('Failed to parse user data:', e);
        }
      }

      // Use auth context to complete login
      const loginSuccess = await login(decodeURIComponent(token), userData);

      if (loginSuccess) {
        setAuthMessage(isNewUser ? t("auth.welcomeNewUser") : t("auth.welcomeBack"));
        
        // Clear URL parameters and redirect
        setTimeout(() => {
          navigate('/user/profile', { replace: true });
        }, 1500);
      } else {
        throw new Error('Failed to complete login process');
      }

    } catch (error) {
      console.error('Google auth completion error:', error);
      setAuthError(t("errors.googleAuthCompletionFailed"));
      // Clear URL parameters
      navigate('/signin', { replace: true });
    }
  }, [t, login, navigate]);

  useEffect(() => {
    updatePageTitle(t("auth.signIn"), t("auth.loginToAccount"));

    // Check for Google OAuth callback parameters
    const success = searchParams.get('success');
    const token = searchParams.get('token');
    const user = searchParams.get('user');
    const error = searchParams.get('error');
    const message = searchParams.get('message');
    const isNewUser = searchParams.get('new_user');

    if (success === 'true' && token) {
      // Google authentication successful
      handleGoogleAuthSuccess(token, user, isNewUser === 'true');
    } else if (error) {
      // Google authentication failed
      setAuthError(message || t("errors.googleAuthFailed"));
      // Clear URL parameters
      navigate('/signin', { replace: true });
    }
  }, [updatePageTitle, searchParams, login, navigate, t, handleGoogleAuthSuccess]);

  /**
   * Handle successful Google authentication
   * Called when Google login completes successfully
   */
  const handleGoogleSuccess = () => {
    console.log("Google sign-in successful");
    // Navigation is handled by the GoogleBtn component
  };

  /**
   * Handle Google authentication error
   * Called when Google login fails
   */
  const handleGoogleError = (error) => {
    console.error("Google sign-in error:", error);
    // Error display is handled by the GoogleBtn component
  };

  return (
    <div className="max-w-6xl mx-auto signin">
      {/* Success/Error Messages */}
      {authMessage && (
        <div className="fixed top-4 right-4 z-50 bg-green-50 border border-green-200 rounded-lg p-4 shadow-lg">
          <div className="flex items-center">
            <svg className="w-5 h-5 text-green-400 mr-3" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <p className="text-sm font-medium text-green-800">{authMessage}</p>
          </div>
        </div>
      )}
      
      {authError && (
        <div className="fixed top-4 right-4 z-50 bg-red-50 border border-red-200 rounded-lg p-4 shadow-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <svg className="w-5 h-5 text-red-400 mr-3" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <p className="text-sm font-medium text-red-800">{authError}</p>
            </div>
            <button
              onClick={() => setAuthError('')}
              className="ml-3 text-red-400 hover:text-red-600"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Main Content Grid */}
      <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
        
        {/* Sign In Form Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-100 ">
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-2">{t("auth.welcomeBack")}</h2>
            <p className="text-gray-600">{t("auth.loginToAccount")}</p>
          </div>
          <SignInForm />
        </div>

        {/* Alternative Options Section */}
        <div className="space-y-6">
          
          {/* Google Sign In */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6 lg:p-8">
            <h3 className="text-xl font-semibold text-gray-800 mb-4 text-center">
              {t("auth.quickSignIn")}
            </h3>
            <div className="flex justify-center" style={{padding:"10px",margin:"10px"}}>
              <GoogleBtn
                text={t("auth.signInWithGoogle")}
                onSuccess={handleGoogleSuccess}
                onError={handleGoogleError}
                className="w-full max-w-sm"
              />
            </div>
          </div>

          {/* Sign Up Option */}
          <div className="bg-gradient-to-br from-orange-50 to-red-50 rounded-lg border border-orange-100 p-6 lg:p-8">
            <h3 className="text-xl font-semibold text-gray-800 mb-4 text-center">
              {t("auth.newToPlatform")}
            </h3>
            <p className="text-gray-600 text-center mb-6">
              {t("auth.joinThousands")}
            </p>
            <div className="flex justify-center">
              <Link
                to="/signup"
                className="bg-[#F15213] hover:bg-[#F15213]/90 text-white font-semibold px-8 py-3 rounded-lg transition-colors shadow-md hover:shadow-lg"
              >
                {t("auth.createAccount")}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
